<script lang="ts">
  import type { ThumbnailEmptyOptions, ThumbnailRadius } from './ThumbnailGroup.types';

  export * from '../../composables/useSortable/useSortable';
  export * from './ThumbnailGroup.types';
</script>

<script setup lang="ts" generic="Thumbnail extends ThumbnailDefinition">
  import { computed, provide, ref } from 'vue';

  import useSortable, {
    moveSortableElements,
    type SortableOnDragEndEvent,
    type SortableOnDragStartEvent,
  } from '../../composables/useSortable/useSortable';
  import { ThumbnailDefinition } from '../Thumbnail/Thumbnail.types';
  import ThumbnailEmpty from '../ThumbnailEmpty/ThumbnailEmpty.vue';
  import { THUMBNAIL_INJECTION } from './ThumbnailGroup.keys';

  const emit = defineEmits<{
    /**
     * Emits when a thumbnail starts dragging
     */
    dragStart: [event: SortableOnDragStartEvent];

    /**
     * Emits when a thumbnail stops dragging
     */
    dragEnd: [event: SortableOnDragEndEvent];

    /**
     * Emits when the thumbnails are updated
     */
    'update:thumbnails': [thumbnails: Thumbnail[]];
  }>();

  const props = withDefaults(
    defineProps<{
      /**
       * Enable drag and drop functionality
       * @default false
       */
      draggable?: boolean;

      /**
       * Thumbnail radius. Can be 'rounded' or 'circle'
       * @default 'rounded'
       */
      radius?: ThumbnailRadius;

      /**
       * Shows a close button on the thumbnail when set to true
       * @default false
       */
      removable?: boolean;

      /**
       * Shows empty thumbnails when set to true
       */
      showEmpty?: boolean;

      /**
       * Empty thumbnail options. Only applicable when `showEmpty` is true
       * @default { max: 6 }
       */
      emptyOptions?: ThumbnailEmptyOptions;

      /**
       * List of thumbnails to display
       * @default []
       */
      thumbnails?: Thumbnail[];
    }>(),
    {
      thumbnails: () => [],
      draggable: false,
      radius: 'rounded',
      variant: 'opaque',
      removable: false,
      showEmpty: false,
      emptyOptions: () => ({ max: 6 }),
    },
  );

  /**
   * The active thumbnail index
   */
  const activeThumbnail = defineModel<number>('activeThumbnail', {
    default: -1,
  });

  const internalThumbnails = computed({
    get() {
      return props.thumbnails;
    },
    set(value) {
      emit('update:thumbnails', value);
    },
  });

  const thumbnailRef = ref<HTMLElement | null>(null);
  const { isDragging } = useSortable({
    ref: thumbnailRef,
    list: internalThumbnails,
    isEnabled: props.draggable,
    ghostClass: 'thumbnail-ghost',
    chosenClass: 'thumbnail-chosen',
    sortInPlace: false,
    onDragStart: (e) => emit('dragStart', e),
    onDragEnd: (e) => {
      activeThumbnail.value = e.newIndex;
      moveSortableElements({ list: internalThumbnails, from: e.oldIndex, to: e.newIndex });
      emit('dragEnd', e);
    },
  });

  const emptyThumbnailsCount = computed(() => {
    if (!props.showEmpty) return 0;

    const { max } = props.emptyOptions || {};

    return internalThumbnails.value.length < max ? max - internalThumbnails.value.length : 0;
  });

  provide(
    THUMBNAIL_INJECTION.key,
    Object.freeze({
      draggable: computed(() => props.draggable),
      removable: computed(() => props.removable),
      thumbnails: internalThumbnails,
      radius: props.radius,
      activeThumbnail,
      isDragging,
    }),
  );
</script>

<template>
  <ul ref="thumbnailRef" class="stash-thumbnail-group flex flex-wrap gap-3" data-test="stash-thumbnail-group">
    <slot v-bind="{ activeThumbnail, isDragging }"></slot>

    <ThumbnailEmpty v-for="(_, index) in emptyThumbnailsCount" :key="`empty_thumb_${index}`" />
  </ul>
</template>

<style>
  @layer utilities {
    /* Ghost is cloned to document.body (see createGhostElement); must not require .stash-thumbnail-group ancestor */
    .thumbnail-ghost {
      .stash-thumbnail-item__img {
        border: 1px solid var(--color-blue-500);
        mask-image: none !important;
        box-shadow: var(--shadow-sm);
      }

      .stash-thumbnail-item__remove {
        opacity: 0;
      }
    }
  }
</style>

<style module>
  @layer utilities {
    :global(:where(.stash-thumbnail-group) .thumbnail-chosen) {
      opacity: 0 !important;
    }
  }
</style>
